import { useRef, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { SendIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useSnapshot } from 'valtio';
import * as yup from 'yup';

import { msCatalystApi } from '../../../../services/api/ms-catalyst-api.js';
import { authState } from '../../../../services/state/auth-state.jsx';
import {
  loginState,
  loginType,
} from '../../../../services/state/login-state.jsx';
import { CATALYST_BACKOFFICEUSER_ROLES } from '../../../../utils/constants.js';

import {
  turnstileStatusState,
  TurnstileWidget,
} from '../../../turnstile-widget.jsx';
import { Button } from '../../../ui/button.jsx';
import FormFooter from '../../../ui/form-footer.jsx';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../ui/form.jsx';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from '../../../ui/input-otp.jsx';
import { Input } from '../../../ui/input.jsx';

export const FormLoginWithOtp = () => {
  const navigate = useNavigate();
  const [isEmailSent, setIsEmailSent] = useState(false);
  const formRef = useRef();
  const turnstileRef = useRef();
  const formLoginRef = useRef();
  const turnstileLoginRef = useRef();
  const { status: turnstileStatus, setStatus: setTurnstileStatus } =
    useSnapshot(turnstileStatusState);

  const formLoginEmailToSendTotp = useForm({
    resolver: yupResolver(
      yup.object().shape({
        email: yup
          .string()
          .min(1, 'Por favor insira o seu email')
          .email('Email inválido')
          .required(),
      }),
    ),
    defaultValues: {
      email: '',
    },
  });

  const watchEmail = formLoginEmailToSendTotp.watch('email');

  const formLoginWithOtp = useForm({
    resolver: yupResolver(
      yup.object().shape({
        otp: yup
          .string()
          .min(6, 'O código tem de possuir 6 números')
          .required('Por favor insira o código que recebeu no seu email'),
      }),
    ),
    defaultValues: {
      otp: '',
    },
  });

  const mutationRequestLoginOtp = useMutation({
    mutationFn: async (data) => {
      await msCatalystApi.post(`/auth/login/request-otp`, {
        email: data.email,
        turnstile_token: data.turnstileToken,
      });
    },
    onSuccess: () => {
      setIsEmailSent(true);
      toast.success(
        'Código único de acesso enviado com sucesso para o seu email',
      );
    },
    onError: () => {
      toast.error('Ocorreu um erro ao pedir o código único de acesso', {
        description: 'Tente novamente mais tarde',
      });

      setTurnstileStatus('pending');
      turnstileRef.current?.reset();
    },
  });

  const mutationLoginWithOtp = useMutation({
    mutationFn: async (data) => {
      const { data: loggedInUser } = await msCatalystApi.post(
        `/auth/login/otp`,
        {
          email: data.email,
          otp: data.otp,
        },
      );

      return loggedInUser.data;
    },
    onSuccess: (data) => {
      authState.user = data;
      authState.isAdmin =
        data.details.role === CATALYST_BACKOFFICEUSER_ROLES.admin;

      if (authState.isAdmin) {
        navigate('/backoffice');
      } else {
        navigate('/');
      }
    },
    onError: (err) => {
      if (
        err instanceof AxiosError &&
        err.response.data?.message === 'TOTP not found or expired.'
      ) {
        toast.error(
          'O código único de acesso que inseriu não existe ou já expirou',
          {
            description:
              'Por favor peça um novo código ao carregar no botão "Reenviar Código"',
          },
        );

        return;
      }

      toast.error('Ocorreu um erro ao iniciar sessão', {
        description: 'Por favor tente novamente mais tarde',
      });
    },
  });

  async function onSubmitEmailToSendTotp(data) {
    const formData = new FormData(formRef.current);
    const turnstileToken = formData.get('cf-turnstile-response');

    if (!turnstileToken) {
      toast.error('Erro ao iniciar sessão', {
        description:
          'Não foi possível determinar com sucesso a sua veracidade como humano',
      });

      return;
    }

    mutationRequestLoginOtp.mutate({
      email: data.email,
      turnstileToken,
    });
  }

  async function onSubmitEmailToResendTotp() {
    const formData = new FormData(formLoginRef.current);
    const turnstileToken = formData.get('cf-turnstile-response');

    if (!turnstileToken) {
      toast.error('Erro ao iniciar sessão', {
        description:
          'Não foi possível determinar com sucesso a sua veracidade como humano',
      });

      return;
    }

    mutationRequestLoginOtp.mutate({
      email: watchEmail,
      turnstileToken,
    });
  }

  async function onSubmitLoginWithOtp(data) {
    mutationLoginWithOtp.mutate({ email: watchEmail, otp: data.otp });
  }

  return (
    <>
      {!isEmailSent ? (
        <Form {...formLoginEmailToSendTotp}>
          <form
            ref={formRef}
            name="login-send-otp"
            onSubmit={formLoginEmailToSendTotp.handleSubmit(
              onSubmitEmailToSendTotp,
            )}
          >
            <div className="grid gap-3">
              <FormField
                control={formLoginEmailToSendTotp.control}
                name="email"
                render={({ field }) => (
                  <FormItem variant="portal">
                    <FormLabel>E-mail</FormLabel>
                    <FormControl>
                      <Input
                        disabled={
                          formLoginEmailToSendTotp.formState.isSubmitting
                        }
                        {...field}
                      />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="pt-4">
              <TurnstileWidget ref={turnstileRef} />
            </div>

            <div className="pt-8">
              <Button
                type="submit"
                variant="portal"
                className="w-full"
                disabled={
                  !formLoginEmailToSendTotp.formState.isDirty ||
                  mutationRequestLoginOtp.isPending ||
                  turnstileStatus !== 'solved'
                }
                isLoading={mutationRequestLoginOtp.isPending}
              >
                Enviar código único de acesso
              </Button>
            </div>
          </form>
        </Form>
      ) : (
        <Form {...formLoginWithOtp}>
          <form
            ref={formLoginRef}
            onSubmit={formLoginWithOtp.handleSubmit(onSubmitLoginWithOtp)}
          >
            <FormField
              control={formLoginWithOtp.control}
              name="otp"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Código único de acesso</FormLabel>
                  <FormControl>
                    <InputOTP maxLength={6} {...field}>
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                      </InputOTPGroup>

                      <InputOTPSeparator />

                      <InputOTPGroup>
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                      </InputOTPGroup>

                      <InputOTPSeparator />

                      <InputOTPGroup>
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  </FormControl>

                  <FormMessage />

                  <Button
                    type="button"
                    variant="link"
                    className="px-0 pt-1 text-xs text-muted-foreground"
                    /* TODO: mutation and set the one more pending */
                    /* TODO: create client-side timeout of requesting OTP */
                    disabled={mutationRequestLoginOtp.isPending}
                    onClick={() => onSubmitEmailToResendTotp()}
                    isLoading={mutationRequestLoginOtp.isPending}
                  >
                    <SendIcon />
                    Reenviar código único de acesso
                  </Button>
                </FormItem>
              )}
            />

            <div className="pt-2">
              <TurnstileWidget ref={turnstileLoginRef} />
            </div>

            <FormFooter className="pt-8">
              <Button
                type="submit"
                variant="portal"
                className="w-full"
                disabled={
                  !formLoginWithOtp.formState.isDirty ||
                  formLoginWithOtp.formState.isSubmitting
                }
                isLoading={
                  formLoginWithOtp.formState.isSubmitting ||
                  mutationLoginWithOtp.isPending
                }
              >
                Iniciar sessão
              </Button>
            </FormFooter>
          </form>
        </Form>
      )}

      <div className="flex flex-col gap-y-1 pt-6">
        <Button
          variant="link"
          type="button"
          className="h-auto text-xs font-medium text-muted-foreground hover:opacity-75"
          onClick={() => {
            loginState.type = loginType.password;
          }}
        >
          Iniciar sessão com palavra-passe
        </Button>
      </div>
    </>
  );
};
